'use client'

import { useCallback, useMemo } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'

import useClevertapForHolidays from '@/src/hooks/analytics/clevertap/holidays'
import { HOLIDAY_ACTION, HOLIDAY_ACTION_TYPE } from '@/src/utils/analytics/holidays/holidayEvents'
import {
    HOLIDAY_PACKAGE_FILTER_PARAM,
    type HolidayPackageFilters,
    parseHolidayPackageFilters,
    toggleFilterValue
} from '@/src/utils/holidays/packageFilters'

/**
 * The all-packages filter state, held in the URL rather than in React state.
 *
 * The URL is the only copy: the server renders the first grid from it, every link is shareable and
 * the Back button steps through filter changes. `replace` keeps that history from filling with one
 * entry per checkbox while still updating the address bar.
 */
export const usePackageFilters = () => {
    const searchParams = useSearchParams()
    const router = useRouter()
    const pathname = usePathname()
    const { triggerHolidayUIActionEvent } = useClevertapForHolidays()

    const filters = useMemo(
        () => parseHolidayPackageFilters(Object.fromEntries(searchParams.entries())),
        [searchParams]
    )

    const applyFilters = useCallback(
        (next: HolidayPackageFilters) => {
            // Rebuilt from the live params so anything else on the URL — app mode, a campaign tag —
            // survives a filter change.
            const params = new URLSearchParams(searchParams.toString())
            Object.values(HOLIDAY_PACKAGE_FILTER_PARAM).forEach(param => params.delete(param))
            if (next.cities.length) params.set(HOLIDAY_PACKAGE_FILTER_PARAM.CITY, next.cities.join(','))
            if (next.months.length) params.set(HOLIDAY_PACKAGE_FILTER_PARAM.MONTH, next.months.join(','))
            if (next.occasions.length)
                params.set(HOLIDAY_PACKAGE_FILTER_PARAM.OCCASION, next.occasions.join(','))
            if (next.themes.length) params.set(HOLIDAY_PACKAGE_FILTER_PARAM.THEME, next.themes.join(','))

            const query = params.toString()
            router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false })
        },
        [searchParams, router, pathname]
    )

    const track = useCallback(
        (filterType: string, engagementType: string, label: string, next: HolidayPackageFilters) => {
            triggerHolidayUIActionEvent(HOLIDAY_ACTION.CONTENT_ENGAGED, {
                action_type: HOLIDAY_ACTION_TYPE.CONTENT,
                component: 'packages_filter',
                engagement_type: engagementType,
                component_label: label,
                filter_type: filterType,
                selected_cities: next.cities.join(',') || 'N/A',
                selected_months: next.months.join(',') || 'N/A',
                selected_occasions: next.occasions.join(',') || 'N/A',
                selected_themes: next.themes.join(',') || 'N/A'
            })
        },
        [triggerHolidayUIActionEvent]
    )

    const toggleCity = useCallback(
        (city: string) => {
            const next = { ...filters, cities: toggleFilterValue(filters.cities, city) }
            applyFilters(next)
            track('city', filters.cities.includes(city) ? 'deselect' : 'select', city, next)
        },
        [filters, applyFilters, track]
    )

    const toggleMonth = useCallback(
        (month: string) => {
            const next = { ...filters, months: toggleFilterValue(filters.months, month) }
            applyFilters(next)
            track('month', filters.months.includes(month) ? 'deselect' : 'select', month, next)
        },
        [filters, applyFilters, track]
    )

    const toggleOccasion = useCallback(
        (occasion: string) => {
            const next = { ...filters, occasions: toggleFilterValue(filters.occasions, occasion) }
            applyFilters(next)
            track('occasion', filters.occasions.includes(occasion) ? 'deselect' : 'select', occasion, next)
        },
        [filters, applyFilters, track]
    )

    const toggleTheme = useCallback(
        (theme: string) => {
            const next = { ...filters, themes: toggleFilterValue(filters.themes, theme) }
            applyFilters(next)
            track('theme', filters.themes.includes(theme) ? 'deselect' : 'select', theme, next)
        },
        [filters, applyFilters, track]
    )

    /** Destination and month committed together, as the search bar's one "update" action. */
    const applySearch = useCallback(
        (cities: string[], months: string[]) => {
            const next = { ...filters, cities, months }
            applyFilters(next)
            track('search_bar', 'apply', [...cities, ...months].join(',') || 'N/A', next)
        },
        [filters, applyFilters, track]
    )

    const clearAll = useCallback(() => {
        const next = { cities: [], months: [], occasions: [], themes: [] }
        applyFilters(next)
        track('all', 'clear', 'N/A', next)
    }, [applyFilters, track])

    return {
        filters,
        toggleCity,
        toggleMonth,
        toggleOccasion,
        toggleTheme,
        applySearch,
        clearAll
    }
}

export default usePackageFilters
